/**
 * TabBar — 3-tab bottom navigation (Practice · Discover · History).
 * Visible only outside retreat mode (controlled by RetreatBridge).
 * screen-integration-009
 */
import styles from "./TabBar.module.css";
import { TEST_IDS } from "../testing/testIds";

type TabId = "home" | "discover" | "history";

type Props = {
  activeTab: TabId;
  onHome: () => void;
  onDiscover: () => void;
  onHistory: () => void;
};

const TABS: Array<{ id: TabId; label: string; testId: string }> = [
  { id: "home",     label: "Practice", testId: TEST_IDS.TAB_HOME     },
  { id: "discover", label: "Discover", testId: TEST_IDS.TAB_DISCOVER },
  { id: "history",  label: "History",  testId: TEST_IDS.TAB_HISTORY  },
];

function TabIcon({ id, active }: { id: TabId; active: boolean }) {
  const stroke = active ? "var(--rc-clay)" : "var(--rc-ink-3)";
  const sw = active ? 1.6 : 1.3;
  const p = { stroke, strokeWidth: sw, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  switch (id) {
    case "home":
      return (
        <svg width={22} height={22} viewBox="0 0 24 24">
          <path d="M3 14c0-3 4-5 9-5s9 2 9 5v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3z" {...p} />
          <path d="M8 14v-2M16 14v-2" {...p} />
        </svg>
      );
    case "discover":
      return (
        <svg width={22} height={22} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8.5" {...p} />
          <path d="M12 7.5l1.4 3.1L16.5 12l-3.1 1.4L12 16.5l-1.4-3.1L7.5 12l3.1-1.4L12 7.5z" {...p} />
        </svg>
      );
    case "history":
      return (
        <svg width={22} height={22} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8.5" {...p} />
          <path d="M12 7v5l3 2" {...p} />
        </svg>
      );
  }
}

export function TabBar({ activeTab, onHome, onDiscover, onHistory }: Props) {
  const handlers: Record<TabId, () => void> = {
    home: onHome, discover: onDiscover, history: onHistory,
  };

  return (
    <nav className={styles.tabBar} aria-label="Main navigation" data-testid={TEST_IDS.TAB_BAR}>
      {TABS.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
            onClick={handlers[tab.id]}
            type="button"
            aria-label={tab.label}
            aria-current={isActive ? "page" : undefined}
            data-testid={tab.testId}
          >
            <TabIcon id={tab.id} active={isActive} />
            <span className={isActive ? styles.labelActive : styles.label}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
